import React from "react";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { Button, Iconography, Loader } from "@hid-galaxy-ui/galaxy-react";
import StatusMessage from "./status-message";
import { useNavigate, useParams } from "react-router";
import { FormFieldType } from "../../../redux/slices/engagement-form/Types";
import CustomTextField from "../../../common/components/dynamic-form-fields/CustomTextField";
import CustomTextArea from "../../../common/components/dynamic-form-fields/CustomTextArea";
import CustomRadio from "../../../common/components/dynamic-form-fields/CustomRadio";
import CustomCheckBox from "../../../common/components/dynamic-form-fields/CustomCheckBox";
import { LoaderSizes } from "@hid-galaxy-ui/galaxy-react/components/Loader/loaderEnums";
import {
  addFormSalesNoteSagaAction,
  getFormFieldsSalesNoteSagaAction,
  getSalesNoteFormDetailSagaAction,
  updateFormSalesNoteSagaAction,
} from "../../../redux/saga/notes/saga-actions";
import { useSessionCanEditModule } from "../../../hooks/useSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../utils/permissions/partnerManagementPermissionKeys";
import "./SalesPartnerNoteForm.scss";

const SalesPartnerNoteForm: React.FC<{}> = ({}) => {
  const [formData, setFormData] = React.useState<Record<string, any>>({});
  const [formSubmitted, setFormSubmitted] = React.useState<boolean>(false);
  const { formFieldsSalesNote, salesNoteFormDetail, status } = useAppSelector(
    (state) => state.notes
  );
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { partnerID } = useParams();
  const numberPartnerID = Number(partnerID);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const canEditPartnerNotes = useSessionCanEditModule(PM_KEYS.PARTNER_NOTES);

  React.useEffect(() => {
    dispatch(getFormFieldsSalesNoteSagaAction());
    dispatch(getSalesNoteFormDetailSagaAction(numberPartnerID));
    window.scrollTo({ top: 0 });
  }, []);

  React.useEffect(() => {
    if (formFieldsSalesNote?.length > 0) {
      setFormData(getInitialFormData(formFieldsSalesNote));
    }
  }, [formFieldsSalesNote]);

  React.useEffect(() => {
    if (formFieldsSalesNote?.length > 0 && salesNoteFormDetail) {
      const trueLabels = salesNoteFormDetail.data
        .filter((item) => item.value === true)
        .map((item) => item.label);

      setFormData({
        [formFieldsSalesNote[0].field_key]: trueLabels,
      });
    }
  }, [salesNoteFormDetail, formFieldsSalesNote]);

  const sortedFields = [...formFieldsSalesNote].sort(
    (a, b) => a.sort_order - b.sort_order
  );

  const getInitialFormData = (fields: FormFieldType[]) => {
    const initialData: Record<string, any> = {};
    fields.forEach((field) => {
      switch (field.field_type) {
        case "text":
        case "textarea":
        case "radio":
          initialData[field.field_key] = "";
          break;
        case "checkbox":
          initialData[field.field_key] = [];
          break;
        default:
          initialData[field.field_key] = "";
      }
    });
    return initialData;
  };

  const handleChange = (key: string, value: string | string[]) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const renderField = (field: FormFieldType, index: number) => {
    switch (field.field_type) {
      case "text":
        return (
          <CustomTextField
            key={field.field_key + index}
            fieldKey={field.field_key}
            fieldLabel={field.label}
            fieldValue={formData[field.field_key] || ""}
            isRequired={!!field.is_required}
            handleChange={handleChange}
          />
        );
      case "textarea":
        return (
          <CustomTextArea
            key={field.field_key + index}
            fieldKey={field.field_key}
            fieldLabel={field.label}
            fieldValue={formData[field.field_key] || ""}
            isRequired={!!field.is_required}
            handleChange={handleChange}
          />
        );
      case "radio":
        return (
          <CustomRadio
            key={field.field_key + index}
            fieldKey={field.field_key}
            fieldLabel={field.label}
            fieldValue={formData[field.field_key] || ""}
            options={field.metadata}
            isRequired={!!field.is_required}
            handleChange={handleChange}
          />
        );
      case "checkbox":
        return (
          <CustomCheckBox
            key={field.field_key + index}
            fieldKey={field.field_key}
            fieldLabel={field.label}
            fieldValue={formData[field.field_key] || ""}
            options={field.metadata}
            isRequired={!!field.is_required}
            handleChange={handleChange}
          />
        );
      default:
        return (
          <CustomTextField
            key={field.field_key + index}
            fieldKey={field.field_key}
            fieldLabel={field.label}
            fieldValue={formData[field.field_key] || ""}
            isRequired={!!field.is_required}
            handleChange={handleChange}
          />
        );
    }
  };

  const onSubmitClick = () => {
    const transformedData = sortedFields.flatMap((field) =>
      field.metadata.map((metaItem) => ({
        label: metaItem,
        value: formData.renew_reason.includes(metaItem),
      }))
    );
    if (
      salesNoteFormDetail &&
      salesNoteFormDetail.partner_id === numberPartnerID
    )
      dispatch(
        updateFormSalesNoteSagaAction({
          id: salesNoteFormDetail.id,
          source: "UserPartnerNote",
          data: transformedData,
        })
      );
    else
      dispatch(
        addFormSalesNoteSagaAction({
          source: "UserPartnerNote",
          submitted_by: loggedUserDetail?.ID,
          partner_id: numberPartnerID,
          data: transformedData,
        })
      );
    setFormSubmitted(true);
  };

  return (
    <div className="sales-partner-note-form-container">
      {formSubmitted ? (
        <StatusMessage />
      ) : (
        <>
          <div className="form-wrapper">
            <h1 className="title">
              {`Why do you think reasons for which partners might not 
                renew their Professional Services (PS) contracts?`}
            </h1>
            {status.formFieldsSalesNoteLoading ? (
              <div className="form-loading">
                <Loader variant={LoaderSizes.large} />
              </div>
            ) : (
              <>
                <form className="form-container">
                  {sortedFields.map((field, index) => {
                    return renderField(field, index);
                  })}
                </form>
                <div className="button-group">
                  <Button
                    variant="secondary"
                    label="Cancel"
                    onClick={() => {
                      navigate(-1);
                    }}
                  />
                  {canEditPartnerNotes && (
                    <Button
                      variant="primary"
                      label="Submit"
                      icon={<Iconography icon="arrowRight" />}
                      onClick={() => {
                        onSubmitClick();
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SalesPartnerNoteForm;
